import * as React from 'react';
import { Spinner, Alert, Tooltip, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { TK } from '../../store/translations/translationKeys';
import { useTranslations } from '../../store/translations/hooks';
import { ConsentResultData, ConsentManagementSettingsData, ClientFile } from '../../models';
import { getConsentShortLabel } from '../../shared/consentUtils';
import { postSendConsentApprovalAsync, ConsentDeliveryChannel } from '../../ajax/requests';
import { useDispatch, useSelector } from 'react-redux';
import { alertError, alertSuccess } from '../../store/alerts/actions';
import { getErrorMessage } from '../../ajax/errors';
import { useCallback } from 'react';
import UploadConsentDocumentModal from '../UploadConsentDocumentModal';
import { getCommunicationQuotas } from '../../store/general/selectors';
import { loadCommunicationQuotasIfNeeded } from '../../store/general/actions';

export interface ClientConsentBadgesProps {
    clientId: string;
    consentResult?: ConsentResultData;
    consentEmailSentAt?: string | Date;
    consentSmsSentAt?: string | Date;
    consentWhatsAppSentAt?: string | Date;
    clientEmail?: string;
    onConsentEmailSent?: (sentAt: Date) => void;
    consentSettings?: ConsentManagementSettingsData;
    onPhysicalConsentUploaded?: (file: ClientFile) => void;
}

const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacer-XXS)',
    padding: 'var(--spacer-XXS) var(--spacer-XS)',
    borderRadius: '12px',
    fontSize: '0.8em',
    backgroundColor: 'var(--appLigthGrey)',
    color: 'var(--text-body)',
    border: 'var(--border-thin) solid var(--appLigthGrey)',
    cursor: 'help',
};

const approvedBadgeStyle: React.CSSProperties = {
    ...badgeStyle,
    backgroundColor: 'var(--success-bg)',
    color: 'var(--success-color)',
    border: 'var(--border-thin) solid var(--success-border)',
};

const ClientConsentBadges: React.FC<ClientConsentBadgesProps> = ({
    clientId,
    consentResult,
    consentEmailSentAt,
    consentSmsSentAt,
    consentWhatsAppSentAt,
    clientEmail,
    onConsentEmailSent,
    consentSettings,
    onPhysicalConsentUploaded,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const communicationQuotas = useSelector(getCommunicationQuotas);

    React.useEffect(() => {
        if (communicationQuotas) {
            return;
        }
        dispatch(loadCommunicationQuotasIfNeeded());
    }, [communicationQuotas, dispatch]);

    const hasWhatsAppQuota = !communicationQuotas || communicationQuotas.whatsAppUsed < communicationQuotas.whatsAppAllowed;
    const hasSmsQuota = !communicationQuotas || communicationQuotas.smsUsed < communicationQuotas.smsAllowed;

    const [sending, setSending] = React.useState(false);
    const [dropdownOpen, setDropdownOpen] = React.useState(false);
    const [showUploadModal, setShowUploadModal] = React.useState(false);

    const [localEmailSentAt, setLocalEmailSentAt] = React.useState<Date | null>(
        consentEmailSentAt ? new Date(consentEmailSentAt) : null
    );
    const [localSmsSentAt, setLocalSmsSentAt] = React.useState<Date | null>(
        consentSmsSentAt ? new Date(consentSmsSentAt) : null
    );
    const [localWhatsAppSentAt, setLocalWhatsAppSentAt] = React.useState<Date | null>(
        consentWhatsAppSentAt ? new Date(consentWhatsAppSentAt) : null
    );

    const [consentError, setConsentError] = React.useState<string | null>(null);
    const [emailReminderTooltipOpen, setEmailReminderTooltipOpen] = React.useState(false);
    const [smsReminderTooltipOpen, setSmsReminderTooltipOpen] = React.useState(false);
    const [whatsAppReminderTooltipOpen, setWhatsAppReminderTooltipOpen] = React.useState(false);
    const [approvedTooltipsOpen, setApprovedTooltipsOpen] = React.useState<Record<string, boolean>>({});
    const [whatsAppQuotaTooltipOpen, setWhatsAppQuotaTooltipOpen] = React.useState(false);
    const [smsQuotaTooltipOpen, setSmsQuotaTooltipOpen] = React.useState(false);

    React.useEffect(() => {
        setConsentError(null);
        setSending(false);
        setLocalEmailSentAt(consentEmailSentAt ? new Date(consentEmailSentAt) : null);
        setLocalSmsSentAt(consentSmsSentAt ? new Date(consentSmsSentAt) : null);
        setLocalWhatsAppSentAt(consentWhatsAppSentAt ? new Date(consentWhatsAppSentAt) : null);
    }, [clientId, consentEmailSentAt, consentSmsSentAt, consentWhatsAppSentAt]);

    const handleSend = useCallback(async (channel: ConsentDeliveryChannel) => {
        if (sending) return;
        if (channel === 'Email' && !clientEmail?.trim()) {
            const message = t(TK.clientEmailRequiredForConsentApproval);
            setConsentError(message);
            dispatch(alertError(message));
            return;
        }

        setConsentError(null);
        setSending(true);

        try {
            const res = await postSendConsentApprovalAsync(clientId, channel);
            const sentAt = res?.sentAt ? new Date(res.sentAt) : new Date();

            if (channel === 'Email') {
                setLocalEmailSentAt(sentAt);
                if (onConsentEmailSent) onConsentEmailSent(sentAt);
            } else if (channel === 'WhatsApp') {
                setLocalWhatsAppSentAt(sentAt);
            } else {
                setLocalSmsSentAt(sentAt);
            }

            dispatch(alertSuccess(t(TK.consentRequestSent)));
        } catch (err: unknown) {
            const message = getErrorMessage(err) || t(TK.somethingWentWrong);
            setConsentError(message);
            dispatch(alertError(message));
        } finally {
            setSending(false);
        }
    }, [clientEmail, clientId, dispatch, sending, t, onConsentEmailSent]);

    const lastSentLabel = (() => {
        const dates = [
            localEmailSentAt ? { label: 'Email', date: localEmailSentAt } : null,
            localWhatsAppSentAt ? { label: 'WhatsApp', date: localWhatsAppSentAt } : null,
            localSmsSentAt ? { label: 'SMS', date: localSmsSentAt } : null,
        ].filter(Boolean) as { label: string; date: Date }[];

        if (!dates.length) return null;
        dates.sort((a, b) => b.date.getTime() - a.date.getTime());
        const { label, date } = dates[0];
        return `${label} · ${date.toLocaleDateString()}`;
    })();

    const sendDropdown = (
        <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(o => !o)} style={{ flexShrink: 0 }}>
            <DropdownToggle
                disabled={sending}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacer-XXS)',
                    padding: 'var(--spacer-XXS) var(--spacer-XS)',
                    borderRadius: '12px',
                    fontSize: '0.75em',
                    backgroundColor: 'var(--appBlue)',
                    color: '#fff',
                    border: 'none',
                    cursor: sending ? 'not-allowed' : 'pointer',
                }}
                tag="span"
            >
                {sending ? <Spinner size="sm" style={{ marginRight: 'var(--spacer-XXS)' }} /> : null}
                {consentResult?.approvedDate ? t(TK.resendConsentRequest) : t(TK.sendConsentApproval)} ▾
            </DropdownToggle>
            <DropdownMenu>
                <DropdownItem onClick={() => handleSend('Email')}>{t(TK.sendConsentViaEmail)}</DropdownItem>
                <span id={`consent-whatsapp-quota-${clientId}`} style={{ display: 'block' }}>
                    <DropdownItem disabled={!hasWhatsAppQuota} onClick={() => handleSend('WhatsApp')}>{t(TK.sendConsentViaWhatsApp)}</DropdownItem>
                </span>
                {!hasWhatsAppQuota && (
                    <Tooltip
                        placement="right"
                        isOpen={whatsAppQuotaTooltipOpen}
                        target={`consent-whatsapp-quota-${clientId}`}
                        toggle={() => setWhatsAppQuotaTooltipOpen(o => !o)}
                    >
                        {t(TK.googleReviewWhatsAppQuotaExhaustedTooltip)}
                    </Tooltip>
                )}
                <span id={`consent-sms-quota-${clientId}`} style={{ display: 'block' }}>
                    <DropdownItem disabled={!hasSmsQuota} onClick={() => handleSend('SMS')}>{t(TK.sendConsentViaSms)}</DropdownItem>
                </span>
                {!hasSmsQuota && (
                    <Tooltip
                        placement="right"
                        isOpen={smsQuotaTooltipOpen}
                        target={`consent-sms-quota-${clientId}`}
                        toggle={() => setSmsQuotaTooltipOpen(o => !o)}
                    >
                        {t(TK.googleReviewSmsQuotaExhaustedTooltip)}
                    </Tooltip>
                )}
                {consentSettings && (
                    <>
                        <DropdownItem divider />
                        <DropdownItem onClick={() => setShowUploadModal(true)}>{t(TK.uploadPhysicalConsent)}</DropdownItem>
                    </>
                )}
            </DropdownMenu>
        </Dropdown>
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            {consentSettings && (
                <UploadConsentDocumentModal
                    clientId={clientId}
                    show={showUploadModal}
                    close={() => setShowUploadModal(false)}
                    consentSettings={consentSettings}
                    onSuccess={file => onPhysicalConsentUploaded && onPhysicalConsentUploaded(file)}
                />
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacer-XS)', flexWrap: 'wrap' }}>
                {consentResult?.approvedDate ? (
                    <>
                        {Object.entries(consentResult.approvedConsents || {})
                            .filter(([_, value]) => value)
                            .map(([key]) => {
                                const label = getConsentShortLabel(key as any, t);
                                const tooltipId = `approved-consent-${key}-${clientId}`;
                                return (
                                    <React.Fragment key={key}>
                                        <span id={tooltipId} style={approvedBadgeStyle}>{label}</span>
                                        <Tooltip
                                            placement="top"
                                            isOpen={approvedTooltipsOpen[key] || false}
                                            target={tooltipId}
                                            toggle={() => setApprovedTooltipsOpen(prev => ({ ...prev, [key]: !prev[key] }))}
                                        >
                                            {`${t(TK.approvedOn)} ${consentResult?.approvedDate ? new Date(consentResult.approvedDate).toLocaleDateString() : ''}`}
                                        </Tooltip>
                                    </React.Fragment>
                                );
                            })}
                        {(consentResult.approvedCustomCheckboxes ?? []).map(({ text }, i) => {
                            const tooltipId = `approved-custom-consent-${i}-${clientId}`;
                            return (
                                <React.Fragment key={text}>
                                    <span id={tooltipId} style={approvedBadgeStyle}>{text}</span>
                                    <Tooltip
                                        placement="top"
                                        isOpen={approvedTooltipsOpen[tooltipId] || false}
                                        target={tooltipId}
                                        toggle={() => setApprovedTooltipsOpen(prev => ({ ...prev, [tooltipId]: !prev[tooltipId] }))}
                                    >
                                        {`${t(TK.approvedOn)} ${consentResult?.approvedDate ? new Date(consentResult.approvedDate).toLocaleDateString() : ''}`}
                                    </Tooltip>
                                </React.Fragment>
                            );
                        })}
                        {sendDropdown}
                    </>
                ) : (
                    <>
                        <span
                            id={`email-reminder-consent-${clientId}`}
                            style={badgeStyle}
                        >
                            {getConsentShortLabel('emailReminders' as any, t)}
                        </span>
                        <Tooltip
                            placement="top"
                            isOpen={emailReminderTooltipOpen}
                            target={`email-reminder-consent-${clientId}`}
                            toggle={() => setEmailReminderTooltipOpen(!emailReminderTooltipOpen)}
                        >
                            {t(TK.consentsAssumedByDefault)}
                        </Tooltip>
                        <span
                            id={`sms-reminder-consent-${clientId}`}
                            style={badgeStyle}
                        >
                            {getConsentShortLabel('smsReminders' as any, t)}
                        </span>
                        <Tooltip
                            placement="top"
                            isOpen={smsReminderTooltipOpen}
                            target={`sms-reminder-consent-${clientId}`}
                            toggle={() => setSmsReminderTooltipOpen(!smsReminderTooltipOpen)}
                        >
                            {t(TK.consentsAssumedByDefault)}
                        </Tooltip>
                        <span
                            id={`whatsapp-reminder-consent-${clientId}`}
                            style={badgeStyle}
                        >
                            {getConsentShortLabel('whatsAppReminders' as any, t)}
                        </span>
                        <Tooltip
                            placement="top"
                            isOpen={whatsAppReminderTooltipOpen}
                            target={`whatsapp-reminder-consent-${clientId}`}
                            toggle={() => setWhatsAppReminderTooltipOpen(!whatsAppReminderTooltipOpen)}
                        >
                            {t(TK.consentsAssumedByDefault)}
                        </Tooltip>
                        {sendDropdown}
                        {lastSentLabel && (
                            <span style={{ fontSize: '0.75em', color: 'var(--text-muted)', fontStyle: 'italic', flexShrink: 0 }}>
                                ({lastSentLabel})
                            </span>
                        )}
                    </>
                )}
            </div>
            {consentError && (
                <Alert color="warning" style={{ padding: 'var(--spacer-XXS)', fontSize: '0.8em', marginBottom: 0, marginTop: 'var(--spacer-XXS)' }}>
                    {consentError}
                </Alert>
            )}
        </div>
    );
};

export default ClientConsentBadges;
